import ArrowOutwardRoundedIcon from '@mui/icons-material/ArrowOutwardRounded';
import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined';
import AttachmentRoundedIcon from '@mui/icons-material/AttachmentRounded';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DoneAllOutlinedIcon from '@mui/icons-material/DoneAllOutlined';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import SegmentOutlinedIcon from '@mui/icons-material/SegmentOutlined';
import ThermostatOutlinedIcon from '@mui/icons-material/ThermostatOutlined';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  type SelectChangeEvent,
  Stack,
  Typography,
} from '@mui/material';
import { parseAttachmentMarkerPayload } from '@prospero/shared/src/assistant/assistant-attachment-marker';
import type {
  AssistantAction,
  AssistantCitation,
  AssistantMessage,
  AssistantReplyOptions,
  AssistantTemperatureReadingDraft,
} from '@prospero/shared/src/assistant/assistant-contract';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useMySignature } from '../../hooks/signature/use-signature-queries';
import { ConfigureSignatureDialog } from '../../pages/document-signing/components/ConfigureSignatureDialog';
import { useDownloadDocument } from '../../pages/documents-archive/hooks/use-documents-archive-mutations';
import type { AssistantConfirmResponse } from '../../services/assistant/assistant.service';
import { getHttpErrorBody, getHttpErrorMessage, getHttpErrorStatus } from '../../utils/error.utils';
import { parseDateKey } from '../calendar/calendar-utils';
import { AssistantMark } from './assistant-mark';
import { AssistantMarkdown } from './assistant-markdown';
import { ConsultantDeadlineLinksCard } from './consultant-deadline-links-card';
import { ConsultantDeadlinesDraftCard } from './consultant-deadlines-draft-card';
import { MyDeadlineLinksCard } from './my-deadline-links-card';

type ConfirmAssistantAction = (actionId: string) => Promise<AssistantConfirmResponse>;

interface AssistantMessageBubbleProps {
  message: AssistantMessage;
  onOpenGuideHit: (action: AssistantAction) => void;
  confirmAction: ConfirmAssistantAction;
  /** Sends the user's option pick as a `[scelta: …]` text marker. */
  onSendOptionsText?: (text: string) => void;
  /** True when the options were already answered or a send is in flight. */
  optionsDisabled?: boolean;
  /**
   * SAME staging endpoint the composer's paperclip uses
   * (`POST /assistant/attachments`) — powers the deterministic "Aggiungi
   * foto" button (`options.photoAction`), which uploads and attaches without
   * the operator typing or sending anything themselves.
   */
  uploadAttachment?: (file: File) => Promise<{ attachmentId: string; filename: string }>;
  /**
   * The SAME pending-attachment slot the composer's own paperclip uses
   * (lifted to `AssistantPanel`) — the deterministic "Aggiungi foto" button
   * stages its upload here instead of auto-sending it, so it shows a
   * thumbnail + removal and rides along with whatever message answers the
   * question next. Omitted call sites (e.g. the label assistant panel,
   * whose options never carry `photoAction`) fall back to an inert no-op.
   */
  photoAttachment?: PendingPhotoAttachment | null;
  onPhotoAttachmentChange?: (attachment: PendingPhotoAttachment | null) => void;
}

type ConfirmStatus = 'idle' | 'pending' | 'success' | 'error';

/** `[scelta: <optionId> <label>]` — the marker the model answers to. */
function choiceMarker(choice: { id: string; label: string }): string {
  return `[scelta: ${choice.id} ${choice.label}]`;
}

/** "postInterventionTemp" → "Post Intervention Temp" (fallback for legacy drafts stored before labels). */
function humanizeEvidenceKey(key: string): string {
  return key.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, (first) => first.toUpperCase());
}

/** Compact default for an NC draft row; "Mostra tutto" reveals the rest before signing. */
const NC_PREVIEW_MAX_CHARS = 120;

/** Quote previews on a manual-section card stay shorter: it's a hint, not content. */
const MANUAL_SECTION_QUOTE_MAX_CHARS = 80;

function truncatePreview(text: string, maxChars: number = NC_PREVIEW_MAX_CHARS): string {
  const normalized = text.trim();
  return normalized.length > maxChars ? `${normalized.slice(0, maxChars).trimEnd()}…` : normalized;
}

/**
 * `recordDate` is a date-only ISO string (`YYYY-MM-DD`, see `nc-document.tool.ts`'s
 * `^\d{4}-\d{2}-\d{2}$` validation) — never a timestamp. `new Date(dateOnly)`
 * parses that as UTC midnight, so rendering it in a negative-offset timezone
 * (e.g. the Americas) rolls the displayed day back by one. Building the Date
 * from the literal calendar components instead keeps the day independent of
 * the browser's offset (`parseDateKey`).
 */
export function parseDateOnlyToLocalDate(dateOnly: string): Date {
  return parseDateKey(dateOnly) ?? new Date(dateOnly);
}

/**
 * 409 codes for which "Riprova" is fuorviante — retrying THIS SAME card can
 * never succeed, only confuse:
 * - `nc_pending_link_conflict`: la NC è già tenuta aperta dalla pending link
 *   di un altro documento, finché quella non è firmata.
 * - `nc_draft_superseded`: le risposte dell'intervista sono cambiate dopo che
 *   questa card è stata generata (correzione post-READY, o il tap sulla chip
 *   "Modifica" — `AssistantNcConfirmService.assertConfirmAllowed`) — la card
 *   valida è un'altra, più recente, nello stesso thread.
 */
const NO_RETRY_CONFLICT_CODES = new Set(['nc_pending_link_conflict', 'nc_draft_superseded']);

function isNoRetryNcConflict(err: unknown): boolean {
  const body = getHttpErrorBody(err);
  return (
    getHttpErrorStatus(err) === 409 &&
    typeof body === 'object' &&
    body !== null &&
    NO_RETRY_CONFLICT_CODES.has((body as { code?: unknown }).code as string)
  );
}

/**
 * Blocking #2 (decision 5, mandatory graphic signature): the backend refuses
 * the confirm with 422 `nc_signature_required` when the operator has no
 * signature saved yet. The card offers a CTA to the settings page instead of
 * a plain "Riprova" — retrying without a signature can never succeed, but
 * saving one and coming back to the SAME card does.
 */
function isNcSignatureRequired(err: unknown): boolean {
  const body = getHttpErrorBody(err);
  return (
    getHttpErrorStatus(err) === 422 &&
    typeof body === 'object' &&
    body !== null &&
    (body as { code?: unknown }).code === 'nc_signature_required'
  );
}

const CATEGORY_LABELS: Record<string, string> = {
  HACCP_TRAINING: 'guida.assistant.draft.catHaccpTraining',
  CALIBRATION: 'guida.assistant.draft.catCalibration',
  HACCP: 'guida.assistant.draft.catHaccp',
  WATER_ANALYSIS: 'guida.assistant.draft.catWaterAnalysis',
  OTHER: 'guida.assistant.draft.catOther',
};

/**
 * Why the operator is declaring the value instead of reading it off the display.
 * `lettura-dichiarata` is the plain case and has no label: the card only names
 * the circumstance when there is one worth putting on the record.
 */
const DECLARED_READING_REASON_LABELS: Record<string, string> = {
  'display-spento': 'guida.assistant.temperatureReading.reasonDisplayOff',
  'attrezzatura-guasta': 'guida.assistant.temperatureReading.reasonEquipmentBroken',
  'non-misurabile': 'guida.assistant.temperatureReading.reasonNotMeasurable',
};

function formatCelsius(value: number): string {
  return `${value} °C`;
}

function formatTemperatureLimits(
  reading: AssistantTemperatureReadingDraft,
  t: ReturnType<typeof useTranslation>['t'],
): string {
  const { minTemperature, maxTemperature } = reading;
  if (minTemperature !== null && maxTemperature !== null) {
    return t('guida.assistant.temperatureReading.limitsRange', {
      max: formatCelsius(maxTemperature),
      min: formatCelsius(minTemperature),
    });
  }
  if (minTemperature !== null) {
    return t('guida.assistant.temperatureReading.limitsMin', {
      min: formatCelsius(minTemperature),
    });
  }
  if (maxTemperature !== null) {
    return t('guida.assistant.temperatureReading.limitsMax', {
      max: formatCelsius(maxTemperature),
    });
  }
  return t('guida.assistant.temperatureReading.limitsNone');
}

type UserMessageSegment =
  | { kind: 'text'; value: string }
  | { kind: 'choice'; label: string }
  | { kind: 'attachment'; filename: string };

const USER_MARKER_PATTERN = /\[(scelta|allegato): ([^\]]+)\]/g;
/** `[nc: <recordId>]` targets the NC for the server: nothing to show the user. */
const NC_TARGET_MARKER_PATTERN = /\s*\[nc:[^\]]*\]/g;

/**
 * Protocol markers (`[scelta: id label]`, `[allegato: id file]`) are transport
 * syntax: they render as chips so the user never sees raw brackets.
 */
function parseUserMessageSegments(text: string): (UserMessageSegment & { id: string })[] {
  const segments: (UserMessageSegment & { id: string })[] = [];
  let cursor = 0;
  let counter = 0;
  const push = (segment: UserMessageSegment) => {
    const id = `${segment.kind}-${counter++}`;
    segments.push({ ...segment, id });
  };
  for (const match of text.matchAll(USER_MARKER_PATTERN)) {
    const start = match.index ?? 0;
    if (start > cursor) {
      push({ kind: 'text', value: text.slice(cursor, start) });
    }
    const payload = match[2];
    if (match[1] === 'scelta') {
      const separator = payload.indexOf(' ');
      push({ kind: 'choice', label: separator === -1 ? '' : payload.slice(separator + 1).trim() });
    } else {
      push({ kind: 'attachment', filename: parseAttachmentMarkerPayload(payload)?.filename ?? '' });
    }
    cursor = start + match[0].length;
  }
  if (cursor < text.length) {
    push({ kind: 'text', value: text.slice(cursor) });
  }
  return segments;
}

function UserMessage({ text }: { text: string }) {
  const segments = parseUserMessageSegments(text.replace(NC_TARGET_MARKER_PATTERN, ''));
  return (
    <Box
      sx={{
        alignSelf: 'flex-end',
        bgcolor: 'action.selected',
        borderRadius: '20px 20px 6px 20px',
        maxWidth: '85%',
        px: 1.75,
        py: 1.25,
      }}
    >
      <Stack spacing={0.75} sx={{ alignItems: 'flex-end' }}>
        {segments.map((segment) => {
          if (segment.kind === 'choice') {
            return (
              <Chip
                color="primary"
                icon={<CheckOutlinedIcon />}
                key={segment.id}
                label={segment.label}
                size="small"
                sx={{ maxWidth: '100%' }}
                variant="outlined"
              />
            );
          }
          if (segment.kind === 'attachment') {
            return (
              <Chip
                icon={<AttachFileOutlinedIcon />}
                key={segment.id}
                label={segment.filename}
                size="small"
                sx={{ maxWidth: '100%' }}
                variant="outlined"
              />
            );
          }
          return (
            <Typography
              key={segment.id}
              sx={{
                color: 'text.primary',
                fontSize: '0.95rem',
                lineHeight: 1.6,
                overflowWrap: 'anywhere',
                whiteSpace: 'pre-wrap',
              }}
              variant="body2"
            >
              {segment.value}
            </Typography>
          );
        })}
      </Stack>
    </Box>
  );
}

/**
 * Screenshot card for a guide-hit action. The backend label is "Topic · Section":
 * split once so the topic reads as the strong line and the section as context
 * underneath. The "Apri nella guida" cue lives in the accessible name only —
 * the arrow and card affordance carry it visually.
 */
function GuideHitActionCard({
  action,
  onOpen,
  t,
}: {
  action: Extract<AssistantAction, { kind: 'OPEN_GUIDE_HIT' }>;
  onOpen: (action: AssistantAction) => void;
  t: ReturnType<typeof useTranslation>['t'];
}) {
  const separatorIndex = action.label.indexOf(' · ');
  const topicLabel = separatorIndex > 0 ? action.label.slice(0, separatorIndex) : action.label;
  const sectionLabel = separatorIndex > 0 ? action.label.slice(separatorIndex + 3) : null;
  const images = action.images ?? [];
  return (
    <Box
      aria-label={`${t('guida.assistant.openGuide', { defaultValue: 'Apri nella guida' })} · ${action.label}`}
      onClick={() => onOpen(action)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen(action);
        }
      }}
      role="button"
      sx={{
        alignSelf: 'flex-start',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        gap: 0.75,
        maxWidth: 280,
        p: 0.75,
        transition: 'border-color 0.15s ease-out, background-color 0.15s ease-out',
        width: images.length > 0 ? 248 : 'auto',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        '&:hover': { bgcolor: 'action.hover', borderColor: 'primary.main' },
      }}
      tabIndex={0}
    >
      {images.length > 0 ? (
        <Box
          sx={{
            display: 'grid',
            gap: 0.5,
            gridTemplateColumns: images.length > 1 ? '1fr 1fr' : '1fr',
          }}
        >
          {images.map((image) => (
            <Box
              alt={image.alt}
              component="img"
              key={image.path}
              src={image.path}
              sx={{
                aspectRatio: '16 / 9',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                display: 'block',
                objectFit: 'cover',
                width: '100%',
              }}
            />
          ))}
        </Box>
      ) : null}
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              color: 'text.primary',
              fontWeight: 600,
              lineHeight: 1.3,
              overflowWrap: 'anywhere',
            }}
            variant="caption"
          >
            {topicLabel}
          </Typography>
          {sectionLabel ? (
            <Typography
              sx={{
                color: 'text.secondary',
                display: '-webkit-box',
                lineHeight: 1.35,
                mt: 0.25,
                overflow: 'hidden',
                overflowWrap: 'anywhere',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 2,
              }}
              variant="caption"
            >
              {sectionLabel}
            </Typography>
          ) : null}
        </Box>
        <ArrowOutwardRoundedIcon
          sx={{ color: 'text.secondary', flexShrink: 0, fontSize: 14, mt: '2px' }}
        />
      </Box>
    </Box>
  );
}

/**
 * Chat CTA for a manual-section jump (OPEN_MANUAL_SECTION): clicking it scrolls
 * the draft page to the mentioned section (and decorates the quote inside the
 * editor). Simpler than `GuideHitActionCard` — no images: the section title is
 * the strong line, the i18n cue and the truncated quote preview (a hint of
 * what will be highlighted) sit underneath, chevron instead of an external
 * arrow since the destination is the page itself.
 */
function ManualSectionActionCard({
  action,
  onOpen,
  t,
}: {
  action: Extract<AssistantAction, { kind: 'OPEN_MANUAL_SECTION' }>;
  onOpen: (action: AssistantAction) => void;
  t: ReturnType<typeof useTranslation>['t'];
}) {
  const cue = t('guida.assistant.actions.openManualSection', {
    defaultValue: 'Vai alla sezione',
  });
  return (
    <Box
      aria-label={`${cue} · ${action.label}`}
      onClick={() => onOpen(action)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen(action);
        }
      }}
      role="button"
      sx={{
        alignSelf: 'flex-start',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        gap: 0.75,
        maxWidth: 280,
        p: 0.75,
        transition: 'border-color 0.15s ease-out, background-color 0.15s ease-out',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        '&:hover': { bgcolor: 'action.hover', borderColor: 'primary.main' },
      }}
      tabIndex={0}
    >
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        <SegmentOutlinedIcon
          sx={{ color: 'text.secondary', flexShrink: 0, fontSize: 16, mt: '2px' }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              color: 'text.primary',
              fontWeight: 600,
              lineHeight: 1.3,
              overflowWrap: 'anywhere',
            }}
            variant="caption"
          >
            {action.label}
          </Typography>
          <Typography
            sx={{
              color: 'text.secondary',
              lineHeight: 1.35,
              mt: 0.25,
              overflowWrap: 'anywhere',
            }}
            variant="caption"
          >
            {cue}
          </Typography>
          {action.quote ? (
            <Typography
              sx={{
                color: 'text.muted',
                display: '-webkit-box',
                fontStyle: 'italic',
                lineHeight: 1.35,
                mt: 0.25,
                overflow: 'hidden',
                overflowWrap: 'anywhere',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 2,
              }}
              variant="caption"
            >
              {truncatePreview(action.quote, MANUAL_SECTION_QUOTE_MAX_CHARS)}
            </Typography>
          ) : null}
        </Box>
        <ChevronRightRoundedIcon
          sx={{ color: 'text.secondary', flexShrink: 0, fontSize: 16, mt: '2px' }}
        />
      </Box>
    </Box>
  );
}

/**
 * Archive-search result card: the label, the total found (the chat lists at
 * most 3) and a button that opens /documents with the same filters applied.
 */
function ArchiveSearchActionCard({
  action,
  t,
}: {
  action: Extract<AssistantAction, { kind: 'OPEN_ARCHIVE_SEARCH' }>;
  t: ReturnType<typeof useTranslation>['t'];
}) {
  const navigate = useNavigate();
  return (
    <Paper
      aria-label={action.label}
      role="region"
      sx={{ alignSelf: 'flex-start', borderRadius: 2, p: 2 }}
      variant="outlined"
    >
      <Stack spacing={0.5}>
        <Typography sx={{ fontWeight: 700 }} variant="body2">
          {action.label}
        </Typography>
        <Typography color="text.secondary" variant="caption">
          {t('guida.assistant.archiveSearch.documentsFound', { count: action.total })}
        </Typography>
      </Stack>
      <Button
        aria-label={t('guida.assistant.archiveSearch.openArchive')}
        onClick={() => navigate(action.url)}
        size="small"
        startIcon={<ArrowOutwardRoundedIcon />}
        sx={{ mt: 1.5 }}
        variant="contained"
      >
        {t('guida.assistant.archiveSearch.openArchive')}
      </Button>
    </Paper>
  );
}

/**
 * Supplier-search result card: the best match's name, the total found (the
 * chat lists at most 3) and a button that opens that supplier's sheet
 * (/suppliers/:id/edit — the same URL the suppliers page uses).
 */
function SupplierActionCard({
  action,
  t,
}: {
  action: Extract<AssistantAction, { kind: 'OPEN_SUPPLIER' }>;
  t: ReturnType<typeof useTranslation>['t'];
}) {
  const navigate = useNavigate();
  return (
    <Paper
      aria-label={action.label}
      role="region"
      sx={{ alignSelf: 'flex-start', borderRadius: 2, p: 2 }}
      variant="outlined"
    >
      <Stack spacing={0.5}>
        <Typography sx={{ fontWeight: 700 }} variant="body2">
          {action.label}
        </Typography>
        <Typography color="text.secondary" variant="caption">
          {t('guida.assistant.supplierSearch.suppliersFound', { count: action.total })}
        </Typography>
      </Stack>
      <Button
        aria-label={t('guida.assistant.supplierSearch.openSupplier')}
        onClick={() => navigate(action.url)}
        size="small"
        startIcon={<ArrowOutwardRoundedIcon />}
        sx={{ mt: 1.5 }}
        variant="contained"
      >
        {t('guida.assistant.supplierSearch.openSupplier')}
      </Button>
    </Paper>
  );
}

/**
 * Customer-search result card: the best match's name, the total found (the
 * chat lists at most 3) and a button that opens that customer's sheet
 * (/customers/:id/edit — the same URL the customers page uses).
 */
function CustomerActionCard({
  action,
  t,
}: {
  action: Extract<AssistantAction, { kind: 'OPEN_CUSTOMER' }>;
  t: ReturnType<typeof useTranslation>['t'];
}) {
  const navigate = useNavigate();
  return (
    <Paper
      aria-label={action.label}
      role="region"
      sx={{ alignSelf: 'flex-start', borderRadius: 2, p: 2 }}
      variant="outlined"
    >
      <Stack spacing={0.5}>
        <Typography sx={{ fontWeight: 700 }} variant="body2">
          {action.label}
        </Typography>
        <Typography color="text.secondary" variant="caption">
          {t('guida.assistant.customerSearch.customersFound', { count: action.total })}
        </Typography>
      </Stack>
      <Button
        aria-label={t('guida.assistant.customerSearch.openCustomer')}
        onClick={() => navigate(action.url)}
        size="small"
        startIcon={<ArrowOutwardRoundedIcon />}
        sx={{ mt: 1.5 }}
        variant="contained"
      >
        {t('guida.assistant.customerSearch.openCustomer')}
      </Button>
    </Paper>
  );
}

/**
 * Generated-report download (self-check audit PDFs, suppliers list). The url
 * is a presigned GET on external storage: the button opens it in a NEW tab,
 * the router never navigates to it.
 */
function DownloadFileCard({
  action,
  t,
}: {
  action: Extract<AssistantAction, { kind: 'DOWNLOAD_FILE' }>;
  t: ReturnType<typeof useTranslation>['t'];
}) {
  return (
    <Paper
      aria-label={action.label}
      role="region"
      sx={{ alignSelf: 'flex-start', borderRadius: 2, p: 2 }}
      variant="outlined"
    >
      <Stack spacing={0.5}>
        <Typography sx={{ fontWeight: 700, overflowWrap: 'anywhere' }} variant="body2">
          {action.label}
        </Typography>
        <Typography color="text.secondary" variant="caption">
          {action.fileName}
        </Typography>
      </Stack>
      <Button
        aria-label={t('guida.assistant.download.button')}
        component="a"
        endIcon={<DownloadRoundedIcon />}
        href={action.url}
        rel="noopener noreferrer"
        size="small"
        sx={{ mt: 1.5 }}
        target="_blank"
        variant="contained"
      >
        {t('guida.assistant.download.button')}
      </Button>
    </Paper>
  );
}

/** Above this many choices the chips row becomes unreadable: render a dropdown instead. */
function ChipsOptionsCard({
  options,
  disabled,
  onSend,
}: {
  options: AssistantReplyOptions;
  disabled: boolean;
  onSend: (text: string) => void;
}) {
  const { t } = useTranslation();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const toggleChoice = (choiceId: string) => {
    setSelectedIds((current) =>
      current.includes(choiceId) ? current.filter((id) => id !== choiceId) : [...current, choiceId],
    );
  };

  const handleConfirmMulti = () => {
    const markers = options.choices
      .filter((choice) => selectedIds.includes(choice.id))
      .map(choiceMarker);
    if (markers.length > 0) {
      onSend(markers.join(', '));
    }
  };

  return (
    <>
      <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.75 }}>
        {options.choices.map((choice) =>
          options.mode === 'single' ? (
            <Chip
              component="button"
              disabled={disabled}
              key={choice.id}
              label={choice.label}
              onClick={() => onSend(choiceMarker(choice))}
              size="small"
              variant="outlined"
            />
          ) : (
            <Chip
              aria-pressed={selectedIds.includes(choice.id)}
              color={selectedIds.includes(choice.id) ? 'primary' : 'default'}
              component="button"
              disabled={disabled}
              key={choice.id}
              label={choice.label}
              onClick={() => toggleChoice(choice.id)}
              size="small"
              variant={selectedIds.includes(choice.id) ? 'filled' : 'outlined'}
            />
          ),
        )}
      </Stack>
      {options.mode === 'multi' ? (
        <Button
          disabled={disabled || selectedIds.length === 0}
          onClick={handleConfirmMulti}
          size="small"
          variant="contained"
        >
          {t('guida.assistant.nc.confirm', { defaultValue: 'Conferma' })}
        </Button>
      ) : null}
    </>
  );
}

/**
 * Same protocol as the chips card (single: sends on pick; multi: accumulates,
 * "Conferma" sends every picked marker), rendered as a MUI Select instead —
 * chips stop being scannable past 3-4 choices (e.g. `causa` with 11 labels).
 * 48px touch target, 16px font (mobile-first rules).
 */
function DropdownOptionsCard({
  options,
  disabled,
  onSend,
}: {
  options: AssistantReplyOptions;
  disabled: boolean;
  onSend: (text: string) => void;
}) {
  const { t } = useTranslation();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const choosePlaceholder = t('guida.assistant.nc.choosePlaceholder', {
    defaultValue: 'Scegli un’opzione',
  });

  const handleSingleChange = (event: SelectChangeEvent<string>) => {
    const choice = options.choices.find((candidate) => candidate.id === event.target.value);
    if (choice) {
      onSend(choiceMarker(choice));
    }
  };

  const handleMultiChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    setSelectedIds(typeof value === 'string' ? value.split(',') : value);
  };

  const handleConfirmMulti = () => {
    const markers = options.choices
      .filter((choice) => selectedIds.includes(choice.id))
      .map(choiceMarker);
    if (markers.length > 0) {
      onSend(markers.join(', '));
    }
  };

  const selectSx = { fontSize: 16, minHeight: 48 };

  return (
    <>
      <FormControl disabled={disabled} size="small" sx={{ minWidth: 220 }}>
        {options.mode === 'single' ? (
          <Select
            aria-label={options.question}
            displayEmpty
            inputProps={{ 'aria-label': options.question }}
            onChange={handleSingleChange}
            renderValue={(value) => value || choosePlaceholder}
            sx={selectSx}
            value=""
          >
            <MenuItem disabled value="">
              {choosePlaceholder}
            </MenuItem>
            {options.choices.map((choice) => (
              <MenuItem key={choice.id} value={choice.id}>
                {choice.label}
              </MenuItem>
            ))}
          </Select>
        ) : (
          <Select
            aria-label={options.question}
            displayEmpty
            inputProps={{ 'aria-label': options.question }}
            multiple
            onChange={handleMultiChange}
            renderValue={(value) =>
              value.length > 0
                ? options.choices
                    .filter((choice) => value.includes(choice.id))
                    .map((choice) => choice.label)
                    .join(', ')
                : choosePlaceholder
            }
            sx={selectSx}
            value={selectedIds}
          >
            {options.choices.map((choice) => (
              <MenuItem key={choice.id} value={choice.id}>
                <Checkbox checked={selectedIds.includes(choice.id)} size="small" />
                <ListItemText primary={choice.label} />
              </MenuItem>
            ))}
          </Select>
        )}
      </FormControl>
      {options.mode === 'multi' ? (
        <Button
          disabled={disabled || selectedIds.length === 0}
          onClick={handleConfirmMulti}
          size="small"
          variant="contained"
        >
          {t('guida.assistant.nc.confirm', { defaultValue: 'Conferma' })}
        </Button>
      ) : null}
    </>
  );
}

/**
 * Structured choices from the assistant: chips at or below the threshold
 * (unchanged behaviour), a dropdown above it — the SERVER decides which via
 * `options.display`, never the client. Free text is always still possible
 * (the composer never disables while an options block is showing); the
 * dropdown adds an explicit hint saying so, since a Select reads as more
 * exclusive than a row of chips with an obviously-still-open text box below.
 */
/** What the deterministic photo button and the composer's own paperclip share as one pending slot. */
export interface PendingPhotoAttachment {
  attachmentId: string;
  filename: string;
  /**
   * Human caption naming the interview field this photo was attached for
   * (e.g. "Foto della merce") — embedded in the `[allegato: … nota:…]`
   * marker on send so the PDF "Prove fotografiche" page can caption it.
   */
  fieldCaption?: string;
}

/**
 * "Aggiungi foto della merce (facoltativa)" → "Foto della merce": the PDF
 * caption reuses the button's own hint text instead of a second,
 * separately-maintained string. Falls back to the raw label when it does
 * not match the pattern — still a valid caption, never blocking.
 */
function deriveFieldCaption(label: string): string {
  const stripped = label
    .replace(/^Aggiungi\s+/i, '')
    .replace(/\s*\(facoltativa\)\s*$/i, '')
    .trim();
  return stripped ? `${stripped.charAt(0).toUpperCase()}${stripped.slice(1)}` : label;
}

/**
 * The deterministic "Aggiungi foto" affordance (`options.photoAction`) — a
 * real button under the question, never a model-authored invitation to use
 * the composer's own paperclip. Opens the device camera/gallery directly
 * (`capture="environment"`), uploads through the SAME staging endpoint the
 * paperclip uses, then stages the result in the SAME pending-attachment slot
 * the composer's own paperclip uses (`photoAttachment`/
 * `onPhotoAttachmentChange`, lifted to `AssistantPanel`) — never auto-sent.
 * A thumbnail + "Foto aggiunta al verbale" + removal (`Chip.onDelete`) show
 * while it is pending; the marker is appended automatically to whatever
 * message the operator sends next (a chip tap or free text) — Fase 3
 * side-fix: the photo is evidence FOR that answer, never a message of its
 * own the operator has to compose. The photo is always optional and never
 * blocks answering the question through the chips/dropdown above.
 */
function PhotoActionButton({
  photoAction,
  disabled,
  uploadAttachment,
  photoAttachment,
  onPhotoAttachmentChange,
}: {
  photoAction: NonNullable<AssistantReplyOptions['photoAction']>;
  disabled: boolean;
  uploadAttachment?: (file: File) => Promise<{ attachmentId: string; filename: string }>;
  photoAttachment: PendingPhotoAttachment | null;
  onPhotoAttachmentChange: (attachment: PendingPhotoAttachment | null) => void;
}) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [status, setStatus] = useState<'idle' | 'uploading' | 'error'>('idle');
  const [failedFilename, setFailedFilename] = useState('');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [myAttachmentId, setMyAttachmentId] = useState<string | null>(null);

  // Revokes the local object URL on removal/unmount — never leaked.
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  if (!uploadAttachment) {
    return null;
  }

  // Only claims the shared slot when IT is the one that filled it — the
  // composer's own generic paperclip (or another instance of this same
  // button, on a different field) may hold it instead.
  const isMine = Boolean(myAttachmentId) && photoAttachment?.attachmentId === myAttachmentId;

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (!file) {
      return;
    }
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);
    setStatus('uploading');
    try {
      const result = await uploadAttachment(file);
      setMyAttachmentId(result.attachmentId);
      onPhotoAttachmentChange({
        attachmentId: result.attachmentId,
        filename: result.filename,
        fieldCaption: deriveFieldCaption(photoAction.label),
      });
      setStatus('idle');
    } catch {
      URL.revokeObjectURL(objectUrl);
      setPreviewUrl(null);
      setFailedFilename(file.name);
      setStatus('error');
    }
  };

  const handleRemove = () => {
    onPhotoAttachmentChange(null);
    setMyAttachmentId(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
  };

  return (
    <Box>
      <input
        accept="image/*"
        capture="environment"
        data-testid={`photo-action-input-${photoAction.fieldKey}`}
        onChange={handleFileSelect}
        ref={fileInputRef}
        style={{ display: 'none' }}
        type="file"
      />
      {isMine ? (
        <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
          {previewUrl ? (
            <Box
              alt=""
              component="img"
              src={previewUrl}
              sx={{ borderRadius: 1, height: 40, objectFit: 'cover', width: 40 }}
            />
          ) : null}
          <Chip
            icon={<CheckOutlinedIcon />}
            label={t('guida.assistant.photoAddedToDocument', {
              defaultValue: 'Foto aggiunta al verbale',
            })}
            size="small"
            sx={{ maxWidth: '100%' }}
            variant="outlined"
          />
          {/* A real, keyboard-focusable button — `Chip.onDelete` renders a
          bare SVG click target with no accessible role (WCAG 2.1 AA). */}
          <IconButton
            aria-label={t('guida.assistant.removePhoto', { defaultValue: 'Rimuovi foto' })}
            onClick={handleRemove}
            size="small"
          >
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </Stack>
      ) : status === 'error' ? (
        <Alert
          action={
            <Button color="inherit" onClick={() => fileInputRef.current?.click()} size="small">
              {t('guida.assistant.retry', { defaultValue: 'Riprova' })}
            </Button>
          }
          severity="error"
        >
          {t('guida.assistant.attachmentFailed', { filename: failedFilename })}
        </Alert>
      ) : (
        <Button
          disabled={disabled || status === 'uploading'}
          onClick={() => fileInputRef.current?.click()}
          size="small"
          startIcon={
            status === 'uploading' ? (
              <CircularProgress color="inherit" size={16} />
            ) : (
              <PhotoCameraOutlinedIcon />
            )
          }
          sx={{ minHeight: 48 }}
          variant="outlined"
        >
          {photoAction.label}
        </Button>
      )}
    </Box>
  );
}

/**
 * NC profiles spell the multi-select instruction inside the question text;
 * the card renders it as its own line from `options.mode`, so it is dropped
 * from the question instead of being shown twice.
 */
const MULTI_SELECT_INSTRUCTION_PATTERN =
  /\s*(?:Puoi scegliere più risposte|You can (?:pick|choose) more than one)\.?\s*$/i;

function OptionsQuestionHeading({ options }: { options: AssistantReplyOptions }) {
  const { t } = useTranslation();
  const isMulti = options.mode === 'multi';
  const question = isMulti
    ? options.question.replace(MULTI_SELECT_INSTRUCTION_PATTERN, '')
    : options.question;
  return (
    <Stack spacing={0.5} sx={{ pt: 0.5, width: '100%' }}>
      <Typography
        component="p"
        sx={{
          color: 'text.primary',
          fontSize: '1rem',
          fontWeight: 600,
          lineHeight: 1.45,
          overflowWrap: 'anywhere',
          textWrap: 'pretty',
        }}
      >
        {question}
      </Typography>
      {isMulti ? (
        <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
          <DoneAllOutlinedIcon aria-hidden sx={{ color: 'primary.main', fontSize: 18 }} />
          <Typography color="text.secondary" variant="body2">
            {t('guida.assistant.nc.multiSelectHint')}
          </Typography>
        </Stack>
      ) : null}
    </Stack>
  );
}

function AssistantOptionsCard({
  options,
  disabled,
  onSend,
  uploadAttachment,
  photoAttachment,
  onPhotoAttachmentChange,
}: {
  options: AssistantReplyOptions;
  disabled: boolean;
  onSend: (text: string) => void;
  uploadAttachment?: (file: File) => Promise<{ attachmentId: string; filename: string }>;
  photoAttachment: PendingPhotoAttachment | null;
  onPhotoAttachmentChange: (attachment: PendingPhotoAttachment | null) => void;
}) {
  const { t } = useTranslation();
  const isDropdown = options.display === 'dropdown';
  // Round 7 fix (G4): the hint used to follow ONLY `isDropdown` — a
  // `choiceOnly` NC field with more than 3 options (a dropdown) invited "oppure
  // scrivi a parole" for a question free text can never answer. Now gated on
  // the server's own `freeTextAllowed` too; the composer itself stays enabled
  // either way, only this misleading hint is withheld.
  const showFreeTextHint = isDropdown && options.freeTextAllowed;
  return (
    <Box
      aria-label={options.question}
      role="group"
      sx={{ alignItems: 'flex-start', display: 'flex', flexDirection: 'column', gap: 1.25 }}
    >
      <OptionsQuestionHeading options={options} />
      {options.choices.length > 0 ? (
        isDropdown ? (
          <DropdownOptionsCard disabled={disabled} onSend={onSend} options={options} />
        ) : (
          <ChipsOptionsCard disabled={disabled} onSend={onSend} options={options} />
        )
      ) : null}
      {showFreeTextHint ? (
        <Typography color="text.secondary" sx={{ overflowWrap: 'anywhere' }} variant="caption">
          {t('guida.assistant.nc.freeTextHint', { defaultValue: 'oppure scrivi a parole' })}
        </Typography>
      ) : null}
      {options.photoAction ? (
        <PhotoActionButton
          disabled={disabled}
          onPhotoAttachmentChange={onPhotoAttachmentChange}
          photoAction={options.photoAction}
          photoAttachment={photoAttachment}
          uploadAttachment={uploadAttachment}
        />
      ) : null}
    </Box>
  );
}

/** One chat action → its card. Kept alone so AssistantMessageBody stays simple. */
function AssistantActionCard({
  action,
  onOpenGuideHit,
  confirmAction,
  onSendMessage,
  t,
}: {
  action: AssistantAction;
  onOpenGuideHit: (action: AssistantAction) => void;
  confirmAction: (actionId: string) => Promise<AssistantConfirmResponse>;
  onSendMessage?: (text: string) => void;
  t: ReturnType<typeof useTranslation>['t'];
}) {
  switch (action.kind) {
    case 'ARCHIVE_DOCUMENT_DRAFT':
      return <ArchiveDraftCard action={action} confirmAction={confirmAction} />;
    case 'GENERATE_NC_DOCUMENT':
      return <NcDocumentCard action={action} confirmAction={confirmAction} />;
    case 'REGISTER_TEMPERATURE_READING':
      return (
        <TemperatureReadingCard
          action={action}
          confirmAction={confirmAction}
          onSendMessage={onSendMessage}
        />
      );
    case 'OPEN_MANUAL_SECTION':
      return <ManualSectionActionCard action={action} onOpen={onOpenGuideHit} t={t} />;
    case 'OPEN_ARCHIVE_SEARCH':
      return <ArchiveSearchActionCard action={action} t={t} />;
    case 'OPEN_SUPPLIER':
      return <SupplierActionCard action={action} t={t} />;
    case 'OPEN_CUSTOMER':
      return <CustomerActionCard action={action} t={t} />;
    case 'DOWNLOAD_FILE':
      return <DownloadFileCard action={action} t={t} />;
    case 'CREATE_CONSULTANT_DEADLINES':
      return <ConsultantDeadlinesDraftCard action={action} />;
    case 'OPEN_CONSULTANT_DEADLINES':
      return <ConsultantDeadlineLinksCard action={action} />;
    case 'OPEN_MY_DEADLINES':
      return <MyDeadlineLinksCard action={action} />;
    default:
      return <GuideHitActionCard action={action} onOpen={onOpenGuideHit} t={t} />;
  }
}

function AssistantMessageBody({
  message,
  onOpenGuideHit,
  confirmAction,
  onSendOptionsText,
  optionsDisabled,
  t,
  uploadAttachment,
  photoAttachment,
  onPhotoAttachmentChange,
}: {
  message: AssistantMessage;
  onOpenGuideHit: (action: AssistantAction) => void;
  confirmAction: (actionId: string) => Promise<AssistantConfirmResponse>;
  onSendOptionsText?: (text: string) => void;
  optionsDisabled: boolean;
  t: ReturnType<typeof useTranslation>['t'];
  uploadAttachment?: (file: File) => Promise<{ attachmentId: string; filename: string }>;
  photoAttachment: PendingPhotoAttachment | null;
  onPhotoAttachmentChange: (attachment: PendingPhotoAttachment | null) => void;
}) {
  // Guide citations whose destination already has an action card are noise:
  // the card carries the same title and link, so the chip row only shows the
  // rest. Only guide hits dedupe — a manual-section card jumps within the
  // draft page and has no citation counterpart.
  const actionUrls = new Set(
    message.actions
      .filter((action) => action.kind === 'OPEN_GUIDE_HIT')
      .map((action) => action.url),
  );
  const visibleCitations = message.citations.filter(
    (citation) => !(citation.kind === 'GUIDE' && actionUrls.has(citation.url)),
  );
  const hasOptions = Boolean(message.options && onSendOptionsText);
  return (
    <Stack direction="row" spacing={1.25} sx={{ alignSelf: 'stretch' }}>
      <AssistantMark iconSize={15} size={26} sx={{ mt: 0.25 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <AssistantMarkdown text={message.text} />
        {message.actions.length > 0 || visibleCitations.length > 0 || hasOptions ? (
          <Stack spacing={0.75} sx={{ mt: 1 }}>
            {message.actions.map((action) => (
              <AssistantActionCard
                action={action}
                confirmAction={confirmAction}
                key={action.id}
                onOpenGuideHit={onOpenGuideHit}
                onSendMessage={onSendOptionsText}
                t={t}
              />
            ))}
            {visibleCitations.map((citation) => (
              <AssistantCitationChip
                citation={citation}
                key={`${message.id}:${JSON.stringify(citation)}`}
                t={t}
              />
            ))}
            {message.options && onSendOptionsText ? (
              <AssistantOptionsCard
                disabled={optionsDisabled}
                onPhotoAttachmentChange={onPhotoAttachmentChange}
                onSend={onSendOptionsText}
                options={message.options}
                photoAttachment={photoAttachment}
                uploadAttachment={uploadAttachment}
              />
            ) : null}
          </Stack>
        ) : null}
      </Box>
    </Stack>
  );
}

export function AssistantMessageBubble({
  message,
  onOpenGuideHit,
  confirmAction,
  onSendOptionsText,
  optionsDisabled = false,
  uploadAttachment,
  photoAttachment = null,
  onPhotoAttachmentChange = () => undefined,
}: AssistantMessageBubbleProps) {
  const { t } = useTranslation();
  const isUser = message.role === 'USER';

  return isUser ? (
    <UserMessage text={message.text} />
  ) : (
    <AssistantMessageBody
      confirmAction={confirmAction}
      message={message}
      onOpenGuideHit={onOpenGuideHit}
      onPhotoAttachmentChange={onPhotoAttachmentChange}
      onSendOptionsText={onSendOptionsText}
      optionsDisabled={optionsDisabled}
      photoAttachment={photoAttachment}
      t={t}
      uploadAttachment={uploadAttachment}
    />
  );
}

function ArchiveDraftCard({
  action,
  confirmAction,
}: {
  action: Extract<AssistantAction, { kind: 'ARCHIVE_DOCUMENT_DRAFT' }>;
  confirmAction: (actionId: string) => Promise<AssistantConfirmResponse>;
}) {
  const { t } = useTranslation();
  const previewDocument = useDownloadDocument();
  const [status, setStatus] = useState<ConfirmStatus>('idle');
  const [confirmedDocumentId, setConfirmedDocumentId] = useState<string | null>(
    action.documentId ?? null,
  );
  const { draft } = action;

  const handleConfirm = async () => {
    setStatus('pending');
    try {
      const result = await confirmAction(action.id);
      setConfirmedDocumentId(result.documentId ?? null);
      setStatus('success');
    } catch {
      setStatus('error');
    }
  };

  const expiresDate = new Date(draft.expiresAt).toLocaleDateString();
  const categoryKey = CATEGORY_LABELS[draft.category] ?? draft.category;

  return (
    <Paper role="region" aria-label={draft.title} variant="outlined" sx={{ borderRadius: 2, p: 2 }}>
      <Stack spacing={0.5}>
        <Typography sx={{ fontWeight: 700 }} variant="body2">
          {draft.title}
        </Typography>
        <Typography color="text.secondary" variant="caption">
          {t('guida.assistant.draft.expiresAt')}: {expiresDate}
        </Typography>
        <Typography color="text.secondary" variant="caption">
          {t('guida.assistant.draft.warningDays', { days: draft.expiryWarningDays })}
        </Typography>
        <Typography color="text.secondary" variant="caption">
          {t('guida.assistant.draft.category')}: {t(categoryKey)}
        </Typography>
        {draft.documentTypeName ? (
          <Typography color="text.secondary" variant="caption">
            {t('guida.assistant.draft.documentType')}: {draft.documentTypeName}
          </Typography>
        ) : null}
        {draft.attachment ? (
          <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.5 }}>
            <AttachmentRoundedIcon color="action" fontSize="small" />
            <Typography color="text.secondary" variant="caption">
              {draft.attachment.filename}
            </Typography>
          </Box>
        ) : null}
      </Stack>
      <Box sx={{ mt: 1.5 }}>
        {confirmedDocumentId ? (
          <Button
            disabled={previewDocument.isPending}
            onClick={() => previewDocument.mutate(confirmedDocumentId)}
            startIcon={
              previewDocument.isPending ? <CircularProgress size={16} /> : <OpenInNewRoundedIcon />
            }
            variant="contained"
          >
            {t('guida.assistant.draft.previewDocument')}
          </Button>
        ) : (
          <Button
            aria-label={t('guida.assistant.draft.confirmCta')}
            disabled={status === 'pending'}
            onClick={handleConfirm}
            startIcon={status === 'pending' ? <CircularProgress size={16} /> : undefined}
            variant="contained"
          >
            {status === 'pending'
              ? t('guida.assistant.draft.confirming')
              : t('guida.assistant.draft.confirmCta')}
          </Button>
        )}
      </Box>
      {status === 'error' ? (
        <Alert
          action={
            <Button color="inherit" onClick={handleConfirm} size="small">
              {t('guida.assistant.retry')}
            </Button>
          }
          severity="error"
          sx={{ mt: 1 }}
        >
          {t('guida.assistant.draft.error')}
        </Alert>
      ) : null}
    </Paper>
  );
}

/**
 * One row of an NC draft (findings, immediate action, product fate, root
 * cause, preventive action, profile evidence) — every field that reaches the
 * signed PDF. Compact by default (`NC_PREVIEW_MAX_CHARS`), but the operator
 * confirms this content into a legal HACCP-register entry, so nothing may
 * stay permanently hidden: a truncated row gets its own "Mostra tutto"
 * toggle, keyboard-reachable and announced via `aria-expanded`.
 */
function NcDraftFieldRow({
  fieldId,
  label,
  value,
}: {
  fieldId: string;
  label: string;
  value: string;
}) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return null;
  }
  const isTruncated = trimmedValue.length > NC_PREVIEW_MAX_CHARS;
  const displayValue = isExpanded || !isTruncated ? trimmedValue : truncatePreview(trimmedValue);
  const textId = `nc-draft-field-${fieldId}`;
  return (
    <Box>
      <Typography
        color="text.secondary"
        id={textId}
        sx={{ overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }}
        variant="caption"
      >
        {label}: {displayValue}
      </Typography>
      {isTruncated ? (
        <Button
          aria-controls={textId}
          aria-expanded={isExpanded}
          onClick={() => setIsExpanded((current) => !current)}
          size="small"
          sx={{ display: 'block', minHeight: 44, mt: 0.25, ml: -1, px: 1, textTransform: 'none' }}
          variant="text"
        >
          {isExpanded
            ? t('guida.assistant.ncDraft.showLess')
            : t('guida.assistant.ncDraft.showMore')}
        </Button>
      ) : null}
    </Box>
  );
}

function NcDraftPhotoRow({
  photos,
}: {
  photos: NonNullable<
    Extract<AssistantAction, { kind: 'GENERATE_NC_DOCUMENT' }>['draft']['photoAttachments']
  >;
}) {
  const { t } = useTranslation();
  return (
    <Typography color="text.secondary" variant="caption">
      {t('guida.assistant.ncDraft.photoAttachments')}:{' '}
      {photos.map((photo) => photo.filename).join(', ')}
    </Typography>
  );
}

/**
 * Round 12, item 3: the server-authored §1 summary when the interview-driven
 * path set one, falling back to the legacy `findings` field unchanged for
 * the model-driven draft tool. A standalone function purely so the `??`
 * lives OUTSIDE `NcDocumentCard`'s own body — kept under the project's Biome
 * cognitive-complexity gate.
 */
function resolveNcDraftSummary(
  draft: Extract<AssistantAction, { kind: 'GENERATE_NC_DOCUMENT' }>['draft'],
): string {
  return draft.ledgerSummary ?? draft.findings;
}

/**
 * Round 12, item 3: the model's own confirmed narrative, shown ONLY when the
 * interview-driven path set a server-authored `ledgerSummary` (§1 already
 * covers the plain findings/describeNc fallback via the "findings" row
 * above, so this never duplicates it) AND the operator actually wrote one.
 * A separate component purely to keep `NcDocumentCard`'s own cognitive
 * complexity under the project's Biome gate.
 */
function NcOperatorNarrativeRow({
  actionId,
  draft,
}: {
  actionId: string;
  draft: Extract<AssistantAction, { kind: 'GENERATE_NC_DOCUMENT' }>['draft'];
}) {
  const { t } = useTranslation();
  if (!draft.ledgerSummary || !draft.operatorNarrative) {
    return null;
  }
  return (
    <NcDraftFieldRow
      fieldId={`${actionId}-operator-narrative`}
      label={t('guida.assistant.ncDraft.operatorNarrative')}
      value={draft.operatorNarrative}
    />
  );
}

/**
 * Confirm state + the only place that actually calls `confirmAction`: a
 * missing signature is handled by reusing the project's existing
 * signature-creation flow (`ConfigureSignatureDialog`, the same dialog
 * `/settings` and the NC document section use) rather than drawing inline —
 * keeps a single "create a signature" UI in the app. A stale `hasSignature`
 * read (the profile signature was deleted between the query and this
 * confirm) surfaces here as the SAME 422 `nc_signature_required` — opening
 * that dialog is the recovery, never a dead-end "Riprova" that could never
 * succeed on its own. Split out of `NcDocumentCard` to keep its own
 * cognitive complexity under the project's Biome gate.
 */
function useNcDocumentConfirm(
  action: Extract<AssistantAction, { kind: 'GENERATE_NC_DOCUMENT' }>,
  confirmAction: ConfirmAssistantAction,
) {
  const { data: signatureData } = useMySignature();
  const hasSignature = Boolean(signatureData?.hasSignature);
  const [status, setStatus] = useState<ConfirmStatus>('idle');
  const [confirmedDocumentId, setConfirmedDocumentId] = useState<string | null>(
    action.documentId ?? null,
  );
  const [configureSignatureOpen, setConfigureSignatureOpen] = useState(false);
  // Errore dell'ultimo confirm: il message del BE quando c'è (409 conflict /
  // 422 firma: già tradotto lato server), e se riprovare può cambiare l'esito.
  const [confirmError, setConfirmError] = useState<{
    message?: string;
    hideRetry: boolean;
  } | null>(null);

  const runConfirm = async () => {
    setStatus('pending');
    setConfirmError(null);
    try {
      const result = await confirmAction(action.id);
      setConfirmedDocumentId(result.documentId ?? null);
      setStatus('success');
    } catch (err) {
      if (isNcSignatureRequired(err)) {
        setStatus('idle');
        setConfigureSignatureOpen(true);
        return;
      }
      setConfirmError({ message: getHttpErrorMessage(err), hideRetry: isNoRetryNcConflict(err) });
      setStatus('error');
    }
  };

  const handlePrimaryCta = () => {
    if (hasSignature) {
      void runConfirm();
    } else {
      setConfigureSignatureOpen(true);
    }
  };

  return {
    hasSignature,
    status,
    confirmedDocumentId,
    confirmError,
    configureSignatureOpen,
    setConfigureSignatureOpen,
    handlePrimaryCta,
  };
}

function NcDocumentCard({
  action,
  confirmAction,
}: {
  action: Extract<AssistantAction, { kind: 'GENERATE_NC_DOCUMENT' }>;
  confirmAction: ConfirmAssistantAction;
}) {
  const { t } = useTranslation();
  const previewDocument = useDownloadDocument();
  const {
    hasSignature,
    status,
    confirmedDocumentId,
    confirmError,
    configureSignatureOpen,
    setConfigureSignatureOpen,
    handlePrimaryCta,
  } = useNcDocumentConfirm(action, confirmAction);
  const { draft } = action;
  const primaryCtaLabel = hasSignature
    ? t('guida.assistant.ncDraft.confirmCta')
    : t('guida.assistant.ncDraft.createSignatureCta');

  const recordDate = parseDateOnlyToLocalDate(draft.recordDate).toLocaleDateString();

  return (
    <Paper role="region" aria-label={draft.title} variant="outlined" sx={{ borderRadius: 2, p: 2 }}>
      <Stack spacing={0.5}>
        <Typography sx={{ fontWeight: 700 }} variant="body2">
          {draft.title}
        </Typography>
        <Typography color="text.secondary" variant="caption">
          {t('guida.assistant.ncDraft.recordDate')}: {recordDate}
        </Typography>
        <NcDraftFieldRow
          fieldId={`${action.id}-findings`}
          label={t('guida.assistant.ncDraft.findings')}
          value={resolveNcDraftSummary(draft)}
        />
        <NcOperatorNarrativeRow actionId={action.id} draft={draft} />
        <NcDraftFieldRow
          fieldId={`${action.id}-immediate-action`}
          label={t('guida.assistant.ncDraft.immediateAction')}
          value={draft.immediateAction}
        />
        <NcDraftFieldRow
          fieldId={`${action.id}-product-fate`}
          label={t('guida.assistant.ncDraft.productFate')}
          value={draft.productFate ?? ''}
        />
        <NcDraftFieldRow
          fieldId={`${action.id}-root-cause`}
          label={t('guida.assistant.ncDraft.rootCause')}
          value={draft.rootCause ?? ''}
        />
        <NcDraftFieldRow
          fieldId={`${action.id}-preventive-action`}
          label={t('guida.assistant.ncDraft.preventiveAction')}
          value={draft.preventiveAction ?? ''}
        />
        {draft.profileEvidence && draft.profileEvidence.length > 0 ? (
          <Stack spacing={0.25} sx={{ mt: 0.5 }}>
            <Typography sx={{ fontWeight: 600 }} variant="caption">
              {t('guida.assistant.ncDraft.profileEvidence')}
            </Typography>
            {draft.profileEvidence.map((evidence) => (
              <NcDraftFieldRow
                fieldId={`${action.id}-evidence-${evidence.key}`}
                key={evidence.key}
                label={evidence.label || humanizeEvidenceKey(evidence.key)}
                value={evidence.value}
              />
            ))}
          </Stack>
        ) : null}
        {draft.photoAttachments?.length ? (
          <NcDraftPhotoRow photos={draft.photoAttachments} />
        ) : null}
      </Stack>
      <Box sx={{ mt: 1.5 }}>
        {confirmedDocumentId ? (
          <Button
            disabled={previewDocument.isPending}
            onClick={() => previewDocument.mutate(confirmedDocumentId)}
            startIcon={
              previewDocument.isPending ? <CircularProgress size={16} /> : <OpenInNewRoundedIcon />
            }
            variant="contained"
          >
            {t('guida.assistant.ncDraft.previewDocument')}
          </Button>
        ) : (
          <Button
            aria-label={primaryCtaLabel}
            disabled={status === 'pending'}
            onClick={handlePrimaryCta}
            startIcon={status === 'pending' ? <CircularProgress size={16} /> : undefined}
            variant="contained"
          >
            {status === 'pending' ? t('guida.assistant.ncDraft.confirming') : primaryCtaLabel}
          </Button>
        )}
      </Box>
      {/* confirmError è non-null solo quando l'ultimo confirm è fallito
          (azzerato a ogni tentativo): è la sola condizione di visibilità. */}
      {confirmError ? (
        <NcConfirmErrorAlert
          hideRetry={confirmError.hideRetry}
          message={confirmError.message}
          onRetry={handlePrimaryCta}
        />
      ) : null}
      <ConfigureSignatureDialog
        onCancel={() => setConfigureSignatureOpen(false)}
        onConfigured={() => setConfigureSignatureOpen(false)}
        open={configureSignatureOpen}
      />
    </Paper>
  );
}

/**
 * Errore del confirm NC: mostra il messaggio tradotto dal BE quando c'è
 * (409 pending link conflict / 422 firma richiesta), altrimenti il testo
 * statico. Il Retry sparisce quando riprovare non può cambiare l'esito.
 */
function NcConfirmErrorAlert({
  message,
  hideRetry,
  onRetry,
}: {
  message?: string;
  hideRetry: boolean;
  onRetry: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Alert
      action={
        hideRetry ? undefined : (
          <Button color="inherit" onClick={onRetry} size="small">
            {t('guida.assistant.retry')}
          </Button>
        )
      }
      severity="error"
      sx={{ mt: 1 }}
    >
      {message ?? t('guida.assistant.ncDraft.error')}
    </Alert>
  );
}

/** Green when compliant, red when not — the same legend as the rest of the app. */
function TemperatureVerdictRow({
  isCompliant,
  isRecorded,
}: {
  isCompliant: boolean;
  isRecorded: boolean;
}) {
  const { t } = useTranslation();
  return (
    <Box sx={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.5 }}>
      <Typography color="text.secondary" variant="caption">
        {isRecorded
          ? t('guida.assistant.temperatureReading.outcome')
          : t('guida.assistant.temperatureReading.expectedOutcome')}
      </Typography>
      <Chip
        color={isCompliant ? 'success' : 'error'}
        icon={isCompliant ? <CheckCircleOutlinedIcon /> : <ErrorOutlineOutlinedIcon />}
        label={
          isCompliant
            ? t('guida.assistant.temperatureReading.compliant')
            : t('guida.assistant.temperatureReading.nonCompliant')
        }
        size="small"
      />
    </Box>
  );
}

/** The circumstance behind a declared value; absent for a plain declared reading. */
function TemperatureReasonRow({ reason }: { reason: string }) {
  const { t } = useTranslation();
  const reasonKey = DECLARED_READING_REASON_LABELS[reason];
  if (!reasonKey) {
    return null;
  }
  return (
    <Typography color="text.secondary" variant="caption">
      {t('guida.assistant.temperatureReading.circumstance')}: {t(reasonKey)}
    </Typography>
  );
}

/** What the reading says: equipment, value, configured limits, circumstance, verdict. */
function TemperatureReadingSummary({
  reading,
  isCompliant,
  isRecorded,
}: {
  reading: AssistantTemperatureReadingDraft;
  isCompliant: boolean;
  isRecorded: boolean;
}) {
  const { t } = useTranslation();
  return (
    <Stack spacing={0.5}>
      <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.5 }}>
        <ThermostatOutlinedIcon color="action" fontSize="small" />
        <Typography color="text.secondary" variant="caption">
          {t('guida.assistant.temperatureReading.heading')}
        </Typography>
      </Box>
      <Typography sx={{ fontWeight: 700 }} variant="body2">
        {reading.equipmentName}
      </Typography>
      <Typography color="text.secondary" variant="caption">
        {t('guida.assistant.temperatureReading.temperature')}
      </Typography>
      <Typography
        sx={{
          color: isCompliant ? 'success.main' : 'error.main',
          fontSize: '1.5rem',
          fontWeight: 700,
          lineHeight: 1.2,
        }}
        variant="body1"
      >
        {formatCelsius(reading.temperature)}
      </Typography>
      <Typography color="text.secondary" variant="caption">
        {t('guida.assistant.temperatureReading.limits')}: {formatTemperatureLimits(reading, t)}
      </Typography>
      <TemperatureReasonRow reason={reading.reason} />
      <TemperatureVerdictRow isCompliant={isCompliant} isRecorded={isRecorded} />
    </Stack>
  );
}

/** Says out loud that nothing is stored yet, and offers the button that stores it. */
function TemperatureConfirmPrompt({
  status,
  onConfirm,
}: {
  status: ConfirmStatus;
  onConfirm: () => void;
}) {
  const { t } = useTranslation();
  const isPending = status === 'pending';
  return (
    <>
      <Alert icon={false} severity="info" sx={{ mt: 1.5 }}>
        {t('guida.assistant.temperatureReading.notRecordedYet')}
      </Alert>
      <Button
        aria-label={t('guida.assistant.temperatureReading.confirmCta')}
        disabled={isPending}
        onClick={onConfirm}
        startIcon={isPending ? <CircularProgress size={16} /> : undefined}
        sx={{ mt: 1.5 }}
        variant="contained"
      >
        {isPending
          ? t('guida.assistant.temperatureReading.confirming')
          : t('guida.assistant.temperatureReading.confirmCta')}
      </Button>
    </>
  );
}

/**
 * Post-confirmation block: the record exists now, and a non-compliant one still
 * needs handling — that button sends a normal user message, so the assistant
 * answers it like any other turn.
 */
function TemperatureRecordedBlock({
  equipmentName,
  isCompliant,
  onSendMessage,
}: {
  equipmentName: string;
  isCompliant: boolean;
  onSendMessage?: (text: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <>
      <Alert severity={isCompliant ? 'success' : 'error'} sx={{ mt: 1.5 }}>
        {isCompliant
          ? t('guida.assistant.temperatureReading.recorded')
          : t('guida.assistant.temperatureReading.recordedNonCompliant')}
      </Alert>
      {isCompliant || !onSendMessage ? null : (
        <Button
          color="error"
          onClick={() =>
            onSendMessage(
              t('guida.assistant.temperatureReading.manageNcMessage', {
                equipment: equipmentName,
              }),
            )
          }
          startIcon={<ErrorOutlineOutlinedIcon />}
          sx={{ mt: 1.5 }}
          variant="contained"
        >
          {t('guida.assistant.temperatureReading.manageNcCta')}
        </Button>
      )}
    </>
  );
}

/**
 * A temperature the operator declared in chat, waiting to be written to the
 * HACCP register. Nothing is stored until this card's button is pressed: a
 * register entry is the operator's declaration of fact, so the confirmation —
 * not the model's turn — is what creates the record.
 */
function TemperatureReadingCard({
  action,
  confirmAction,
  onSendMessage,
}: {
  action: Extract<AssistantAction, { kind: 'REGISTER_TEMPERATURE_READING' }>;
  confirmAction: (actionId: string) => Promise<AssistantConfirmResponse>;
  /** Sends a normal user message in chat — the same channel the option chips use. */
  onSendMessage?: (text: string) => void;
}) {
  const { t } = useTranslation();
  const [status, setStatus] = useState<ConfirmStatus>('idle');
  const [recordedId, setRecordedId] = useState<string | null>(action.recordId ?? null);
  const [recordedCompliant, setRecordedCompliant] = useState<boolean | null>(
    action.isCompliant ?? null,
  );
  const { reading } = action;

  const isRecorded = status === 'success' || recordedId !== null;
  // The server recomputes the verdict on confirm; the preview only stands in until then.
  const isCompliant = recordedCompliant ?? reading.previewCompliant;

  const handleConfirm = async () => {
    setStatus('pending');
    try {
      const result = await confirmAction(action.id);
      setRecordedId(result.recordId ?? null);
      setRecordedCompliant(result.isCompliant ?? reading.previewCompliant);
      setStatus('success');
    } catch {
      setStatus('error');
    }
  };

  return (
    <Paper
      aria-label={reading.equipmentName}
      data-action-id={action.id}
      role="region"
      sx={{ borderRadius: 2, p: 2 }}
      variant="outlined"
    >
      <TemperatureReadingSummary
        isCompliant={isCompliant}
        isRecorded={isRecorded}
        reading={reading}
      />
      {isRecorded ? (
        <TemperatureRecordedBlock
          equipmentName={reading.equipmentName}
          isCompliant={isCompliant}
          onSendMessage={onSendMessage}
        />
      ) : (
        <TemperatureConfirmPrompt onConfirm={handleConfirm} status={status} />
      )}
      {status === 'error' ? (
        <Alert
          action={
            <Button color="inherit" onClick={handleConfirm} size="small">
              {t('guida.assistant.retry')}
            </Button>
          }
          severity="error"
          sx={{ mt: 1 }}
        >
          {t('guida.assistant.temperatureReading.error')}
        </Alert>
      ) : null}
    </Paper>
  );
}

function AssistantCitationChip({
  citation,
  t,
}: {
  citation: AssistantCitation;
  t: ReturnType<typeof useTranslation>['t'];
}) {
  if (citation.kind === 'OFFICIAL') {
    return (
      <Button
        component="a"
        endIcon={<OpenInNewRoundedIcon />}
        href={citation.url}
        rel="noopener noreferrer"
        size="small"
        sx={{ alignSelf: 'flex-start', justifyContent: 'flex-start' }}
        target="_blank"
        variant="text"
      >
        {citation.title}
      </Button>
    );
  }
  if (citation.kind === 'GUIDE') {
    return (
      <Typography color="text.muted" sx={{ display: 'block' }} variant="caption">
        {`${t('guida.assistant.citationGuide', { defaultValue: 'Guida' })}: ${citation.title} · ${citation.sectionTitle}`}
      </Typography>
    );
  }
  return (
    <Typography color="text.muted" sx={{ display: 'block' }} variant="caption">
      {`${t('guida.assistant.citationTenantData', { defaultValue: 'Dati azienda' })} · ${new Date(citation.generatedAt).toLocaleString()}`}
    </Typography>
  );
}
